import React, { useState, useEffect, useRef } from "react";
import {
  Sliders, Type, Save, Edit, Check, X as XIcon,
  RefreshCw, AlertTriangle, Layers, Globe,
  EyeOff, Eye
} from "lucide-react";
import SimpleLabelDragger from "../../services/SimpleLabelDragger";

/**
 * Checks if a label symbol has a rectangular background
 * @param {Object} symbol - The text symbol to check
 * @returns {boolean} - Whether the label has a background rectangle
 */
const hasBackgroundRectangle = (symbol) => {
  if (!symbol) return false;
  // Check for explicit background color property and ensure it's not transparent
  return symbol.backgroundColor !== null && symbol.backgroundColor !== undefined &&
         (Array.isArray(symbol.backgroundColor) ? symbol.backgroundColor[3] > 0 : symbol.backgroundColor.a > 0);
};

/**
 * Gets the background opacity from a symbol
 * @param {Object} symbol - The text symbol to check
 * @returns {number} - The opacity value between 0-1
 */
const getBackgroundOpacity = (symbol) => {
  if (!symbol || !symbol.backgroundColor) return 0.85; // Default opacity

  if (Array.isArray(symbol.backgroundColor) && symbol.backgroundColor.length >= 4) {
    return symbol.backgroundColor[3]; // RGBA array format
  } else if (symbol.backgroundColor && typeof symbol.backgroundColor === 'object' &&
             symbol.backgroundColor.a !== undefined) {
    return symbol.backgroundColor.a; // Object format with alpha
  }

  return 0.85; // Default fallback
};

/**
 * Gets the background border size from a symbol
 * @param {Object} symbol - The text symbol to check
 * @returns {number} - The border size value
 */
const getBackgroundBorderSize = (symbol) => {
  if (!symbol || symbol.borderLineSize === undefined) return 0.1; // Default border size (thin)
  return symbol.borderLineSize;
};

const LabelEditor = ({
  isOpen,
  onClose,
  mapView,
  labelManager,
  labelDragger, // Optional prop - expecting an instance of SimpleLabelDragger
  activeLayer, // Optional: Layer context
}) => {
  console.log("[LabelEditor] Component rendering - CODE VERSION 2.0");
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [fontSize, setFontSize] = useState(10);
  const [position, setPosition] = useState({ latitude: 0, longitude: 0 });
  const [labelText, setLabelText] = useState("");
  const [isMoving, setIsMoving] = useState(false); // For single label move (DEPRECATED)
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [isTextEditing, setIsTextEditing] = useState(false);
  const isClearing = useRef(false); // Track when we're intentionally clearing selection
  const hasInitialized = useRef(false); // Track if we've already done initial cleanup
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true); // Always true for localStorage
  const [allLabelsCount, setAllLabelsCount] = useState(0); // Track total label count
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.85); // Default opacity for backgrounds
  const [borderSize, setBorderSize] = useState(0.1); // Default border size (thin padding)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track unsaved changes
  const [dataPointShape, setDataPointShape] = useState('circle'); // Shape of the data point marker
  const [labelOptions, setLabelOptions] = useState({
    includeVariables: true,      // Whether to include variables in labels
    avoidCollisions: true,       // Whether to avoid label collisions
    visibleAtAllZooms: false,    // Whether labels are visible at all zoom levels
    fontSize: 10,                // Default label font size
    bold: false,                 // Whether to use bold text for labels
    whiteBackground: false,      // Whether to use a solid white background
    showLabelConfig: false       // UI toggle for label settings
  });
  const [draggerInitialized, setDraggerInitialized] = useState(false);
  const [localLabelDragger, setLocalLabelDragger] = useState(null);
  const textInputRef = useRef(null);
  const [allLabelsVisible, setAllLabelsVisible] = useState(true);

  // State for Position Adjustment Mode (formerly Multi-Drag Mode)
  const [isMultiDragMode, setIsMultiDragMode] = useState(false);
  const [draggedLabels, setDraggedLabels] = useState([]); // { graphic, originalPosition, currentPosition }
  const [dragInfo, setDragInfo] = useState({ startPoint: null, draggedLabels: [] });
  const [extraHandlers, setExtraHandlers] = useState([]);
  const [originalNavState, setOriginalNavState] = useState(null); // Used by Position Adjustment Mode
  
  // Store original state for potential cancellation - capture the actual state when editor opens
  const [originalEditedLabels, setOriginalEditedLabels] = useState(null);

  // Capture the original state when the editor first opens and reset when it closes
  useEffect(() => {
    if (isOpen && labelManager && labelManager.editedLabels && originalEditedLabels === null) {
      // Create a deep copy of the current editedLabels state
      const originalState = new Map();
      for (const [key, value] of labelManager.editedLabels) {
        originalState.set(key, { ...value });
      }
      setOriginalEditedLabels(originalState);
      console.log(`[LabelEditor] Captured original state with ${originalState.size} edited labels`);
    } else if (!isOpen && originalEditedLabels !== null) {
      // Reset original state when editor closes
      setOriginalEditedLabels(null);
      console.log(`[LabelEditor] Reset original state on editor close`);
    }
  }, [isOpen, labelManager, originalEditedLabels]);

  // CRITICAL: Notify the dragger when Label Editor opens/closes
  // Right-click dragging is ONLY allowed when Label Editor is open
  useEffect(() => {
    const dragger = localLabelDragger ||
                   (typeof window !== 'undefined' ? window.labelDragger : null);

    if (dragger && typeof dragger.setLabelEditorOpen === 'function') {
      dragger.setLabelEditorOpen(isOpen);
      console.log(`[LabelEditor] Notified dragger that Label Editor is ${isOpen ? 'open' : 'closed'}`);
    }

    // Cleanup: ensure dragger knows editor is closed when component unmounts
    return () => {
      if (dragger && typeof dragger.setLabelEditorOpen === 'function') {
        dragger.setLabelEditorOpen(false);
        console.log(`[LabelEditor] Notified dragger that Label Editor is closed (cleanup)`);
      }
    };
  }, [isOpen, localLabelDragger]);

  const toggleAllLabelsVisibility = () => {
    if (!labelManager || typeof labelManager.getAllLabelGraphics !== 'function') {
      console.warn("[LabelEditor] Cannot toggle visibility: Missing required methods");
      return;
    }
    
    try {
      const allLabels = labelManager.getAllLabelGraphics();
      if (!allLabels || allLabels.length === 0) {
        setStatusMessage({ type: 'info', text: 'No labels found to toggle visibility', timeout: 3000 });
        return;
      }
      
      // Toggle the visibility state
      const newVisibleState = !allLabelsVisible;
      setAllLabelsVisible(newVisibleState);
      setHasUnsavedChanges(true); // Mark as changed
      
      let updateCount = 0;
      
      // Apply visibility to all labels
      allLabels.forEach(label => {
        if (label) {
          label.visible = newVisibleState;
          // CRITICAL: Also update attributes.visible so zoom handlers respect user's choice
          if (label.attributes) {
            label.attributes.visible = newVisibleState;
          }

          // Update in label manager tracking if available
          const labelId = labelManager.getLabelId(label);
          if (labelId && labelManager.editedLabels) {
            const existingData = labelManager.editedLabels.get(labelId) || {};
            labelManager.editedLabels.set(labelId, {
              ...existingData,
              graphic: label,
              visible: newVisibleState
            });
          }
          updateCount++;
        }
      });
      
      // Force refresh
      if (labelManager.refreshLabels) {
        labelManager.refreshLabels();
      } else if (mapView?.graphics) {
        mapView.graphics.refresh();
      }
      
      // Save changes
      if (typeof labelManager.savePositions === 'function') {
        labelManager.savePositions(true);
      }
      
      setStatusMessage({ 
        type: 'success', 
        text: `${newVisibleState ? 'Showed' : 'Hidden'} ${updateCount} labels`, 
        timeout: 3000 
      });
      
      console.log(`[LabelEditor] ${newVisibleState ? 'Showed' : 'Hidden'} ${updateCount} labels`);
    } catch (error) {
      console.error("[LabelEditor] Error toggling label visibility:", error);
      setStatusMessage({ 
        type: 'error', 
        text: `Failed to toggle label visibility: ${error.message}`, 
        timeout: 3000 
      });
    }
  };

  useEffect(() => {
    console.log("[LabelEditor] Dragger initialization effect running", {
      isOpen,
      hasMapView: !!mapView,
      hasLabelManager: !!labelManager,
      draggerInitialized
    });

    // Only try to initialize if the editor is open and prerequisites are available
    if (!isOpen || !mapView || !labelManager) {
      console.log("[LabelEditor] Skipping dragger init - prerequisites not met");
      return;
    }

    // Only initialize once
    if (draggerInitialized) {
      console.log("[LabelEditor] Skipping dragger init - already initialized");
      return;
    }
    
    // Check if we already have a dragger from either source
    const existingDragger = localLabelDragger || 
                           (typeof window !== 'undefined' ? window.labelDragger : null);
    
    if (existingDragger && typeof existingDragger.enable === 'function') {
      console.log("[LabelEditor] Label dragger already initialized");
      setLocalLabelDragger(existingDragger);
      setDraggerInitialized(true);
      return;
    }
    
    try {
      console.log("[LabelEditor] Creating a new SimpleLabelDragger instance");
      
      // Create a new SimpleLabelDragger instance directly
      const dragger = new SimpleLabelDragger({
        view: mapView,
        labelManager: labelManager,
        onDragStart: (label) => {
          console.log("[LabelDragger] Started dragging label:", labelManager.getLabelId(label));
          setHasUnsavedChanges(true); // Mark as changed
        },
        onDrag: (label, dx, dy) => {
          // Update is handled internally by the dragger
          console.log("[LabelDragger] Dragging label:", labelManager.getLabelId(label), `delta: (${dx}, ${dy})`);
        },
        onDragEnd: (label) => {
          console.log("[LabelDragger] Finished dragging label:", labelManager.getLabelId(label));
          setHasUnsavedChanges(true); // Mark as changed

          // Update position display after drag
          if (label && label.geometry) {
            setPosition({
              latitude: label.geometry.latitude || label.geometry.y || 0,
              longitude: label.geometry.longitude || label.geometry.x || 0
            });
          }

          // Consider saving after drag ends
          if (labelManager && typeof labelManager.savePositions === 'function') {
            const result = labelManager.savePositions(false);
            // Check if result is a Promise before using .catch
            if (result && typeof result.catch === 'function') {
              result.catch(err => {
                console.error("[LabelDragger] Error saving after drag:", err);
              });
            }
          }
        }
      });
      
      // Initialize and store the dragger
      dragger.initialize();
      setLocalLabelDragger(dragger);
      window.labelDragger = dragger;
      setDraggerInitialized(true);
      console.log("[LabelEditor] SimpleLabelDragger initialized successfully");
    } catch (error) {
      console.error("[LabelEditor] Error creating SimpleLabelDragger:", error);
      
      // Fall back to using the manager's method if direct initialization fails
      if (typeof labelManager.createLabelDragger === 'function') {
        try {
          console.log("[LabelEditor] Falling back to labelManager.createLabelDragger");
          const fallbackDragger = labelManager.createLabelDragger(mapView);
          
          if (fallbackDragger && typeof fallbackDragger.enable === 'function') {
            setLocalLabelDragger(fallbackDragger);
            window.labelDragger = fallbackDragger;
            setDraggerInitialized(true);
            console.log("[LabelEditor] Fallback dragger initialized successfully");
          }
        } catch (fallbackError) {
          console.error("[LabelEditor] Fallback initialization failed:", fallbackError);
        }
      }
    }
  }, [isOpen, mapView, labelManager, draggerInitialized, localLabelDragger]);

  // --- Auto-focus text input when editing ---
  useEffect(() => {
    if (isTextEditing && textInputRef.current) {
      textInputRef.current.focus();
      textInputRef.current.select();
    }
  }, [isTextEditing]);

  // --- Handle status message timeouts ---
  useEffect(() => {
    let timer;
    if (statusMessage && statusMessage.timeout) {
      timer = setTimeout(() => {
        setStatusMessage(null);
      }, statusMessage.timeout);
    }
    return () => clearTimeout(timer);
  }, [statusMessage]);

  // --- Watch for label selection changes ---
  useEffect(() => {
    if (!isOpen || !labelManager) {
      // Reset initialization flag when editor closes
      hasInitialized.current = false;
      return;
    }

    // Clear any previous selection ONLY when editor first opens (not on subsequent effect runs)
    if (!hasInitialized.current) {
      console.log("[LabelEditor] Editor opening - clearing previous selection (synchronous)");
      hasInitialized.current = true;
      isClearing.current = true;
      labelManager.selectedLabel = null;
      setSelectedLabel(null);

      // Clear the flag after a delay
      setTimeout(() => {
        isClearing.current = false;
      }, 150);
    }

    // Define the selection watcher function
    const watchSelection = () => {
      // Skip if we're intentionally clearing the selection
      if (isClearing.current) {
        console.log("[LabelEditor] Skipping selection watcher - intentionally clearing");
        return;
      }

      // Get the currently selected label from the labelManager
      const currentSelectedLabel = labelManager.selectedLabel || null;

      // If it's different from our current state, update
      if (currentSelectedLabel !== selectedLabel) {
        console.log("[LabelEditor] Label selection changed:",
          currentSelectedLabel ? labelManager.getLabelId(currentSelectedLabel) : 'none');

        // Update our state
        setSelectedLabel(currentSelectedLabel);

        // Debug: Check dragger availability
        console.log("[LabelEditor] Dragger check:", {
          hasCurrentLabel: !!currentSelectedLabel,
          hasLocalDragger: !!localLabelDragger,
          draggerType: localLabelDragger?.constructor?.name,
          hasAddHighlights: typeof localLabelDragger?._addHighlights === 'function'
        });

        // Add yellow highlight boxes when a label is selected
        // Try multiple sources for the dragger
        const dragger = localLabelDragger || window.labelDragger || labelDragger;

        if (currentSelectedLabel && dragger) {
          // IMPORTANT: Sync the dragger's selectedLabel for direct drag support
          dragger.selectedLabel = currentSelectedLabel;
        } else if (!currentSelectedLabel && dragger) {
          // IMPORTANT: Clear dragger's selectedLabel when deselecting
          dragger.selectedLabel = null;
        }
        
        // Update position display if selected
        if (currentSelectedLabel && currentSelectedLabel.geometry) {
          const geometry = currentSelectedLabel.geometry;
          const symbol = currentSelectedLabel.symbol; // Define symbol from the selected label
          
          setPosition({
            latitude: geometry.y || 0,
            longitude: geometry.x || 0
          });
          
          // Update font size
          if (symbol && symbol.font && typeof symbol.font.size === 'number') {
            setFontSize(symbol.font.size);
          } else {
            setFontSize(10); // Default
          }
          
          // Update label text
          let textToDisplay = (symbol && symbol.text) ? symbol.text : "";
          if (currentSelectedLabel.attributes?.hasCustomFormat && 
              currentSelectedLabel.attributes?.originalLabel) {
            textToDisplay = currentSelectedLabel.attributes.originalLabel;
          }
          setLabelText(textToDisplay);
          
          // Update style options
          setLabelOptions(prev => ({
            ...prev,
            bold: symbol && symbol.font?.weight === 'bold',
            whiteBackground: hasBackgroundRectangle(symbol)
          }));
          
          // Update background opacity and border size if background is enabled
          if (symbol && symbol.backgroundColor) {
            setBackgroundOpacity(getBackgroundOpacity(symbol));
            setBorderSize(getBackgroundBorderSize(symbol));
          }

          // Update data point shape - find the associated data point and get its shape
          const dataPoint = findDataPointForLabel(currentSelectedLabel);
          if (dataPoint?.symbol?.style) {
            setDataPointShape(dataPoint.symbol.style);
          } else {
            setDataPointShape('circle'); // Default
          }
        }
      }
    };

    // Initial check
    watchSelection();
    
    // Set up a listener for the custom event our dragger dispatches
    const handleLabelSelected = (event) => {
      console.log("[LabelEditor] Label selection event received", event.detail);

      // Get the label directly from the event if available
      const newLabel = event.detail?.label;
      if (newLabel) {
        // Update labelManager's selectedLabel to ensure watchSelection picks it up
        if (labelManager) {
          labelManager.selectedLabel = newLabel;
        }

        // Update local state directly for immediate UI response
        setSelectedLabel(newLabel);

        // Update position display
        if (newLabel.geometry) {
          setPosition({
            latitude: newLabel.geometry.latitude || newLabel.geometry.y || 0,
            longitude: newLabel.geometry.longitude || newLabel.geometry.x || 0
          });
        }

        // Update font size
        const symbol = newLabel.symbol;
        if (symbol && symbol.font && typeof symbol.font.size === 'number') {
          setFontSize(symbol.font.size);
        }

        // Update label text
        let textToDisplay = (symbol && symbol.text) ? symbol.text : "";
        if (newLabel.attributes?.hasCustomFormat && newLabel.attributes?.originalLabel) {
          textToDisplay = newLabel.attributes.originalLabel;
        }
        setLabelText(textToDisplay);

        // Update style options
        setLabelOptions(prev => ({
          ...prev,
          bold: symbol && symbol.font?.weight === 'bold',
          whiteBackground: hasBackgroundRectangle(symbol)
        }));

        // Update background opacity and border size if background is enabled
        if (symbol && symbol.backgroundColor) {
          setBackgroundOpacity(getBackgroundOpacity(symbol));
          setBorderSize(getBackgroundBorderSize(symbol));
        }

        // Update data point shape
        const dataPoint = findDataPointForLabel(newLabel);
        if (dataPoint?.symbol?.style) {
          setDataPointShape(dataPoint.symbol.style);
        } else {
          setDataPointShape('circle');
        }

        // Mark as having unsaved changes since we're in drag mode
        setHasUnsavedChanges(true);
      }

      // Also run normal watchSelection as fallback
      watchSelection();
    };

    // Add event listener
    window.addEventListener('labelSelected', handleLabelSelected);

    // Also set up a polling interval as a fallback
    const intervalId = setInterval(watchSelection, 500);

    // Clean up
    return () => {
      window.removeEventListener('labelSelected', handleLabelSelected);
      clearInterval(intervalId);
    };
  }, [isOpen, labelManager, selectedLabel, localLabelDragger, labelDragger]);

  // --- Cleanup for Position Adjustment Mode ---
  useEffect(() => {
    // This effect specifically handles the cleanup when position adjustment mode is turned off
    // or the component unmounts while it's active.
    return () => {
      if (isMultiDragMode) {
        console.log("[LabelEditor Cleanup Effect] Cleaning up Position Adjustment Mode...");

        // Get the dragger
        const dragger = localLabelDragger ||
                       (typeof window !== 'undefined' ? window.labelDragger : null);

        // Deactivate the dragger if it's available
        if (dragger) {
          try {
            if (dragger instanceof SimpleLabelDragger) {
              dragger.deactivate();
              console.log("[LabelEditor Cleanup] Deactivated SimpleLabelDragger");
            } else if (typeof dragger.enable === 'function') {
              dragger.enable(false);
              console.log("[LabelEditor Cleanup] Disabled position adjustment mode");
            }
          } catch (e) {
            console.warn("[LabelEditor Cleanup] Error disabling position adjustment mode:", e);
          }
        }

        // Remove extra handlers safely
        extraHandlers.forEach(handler => {
          if (handler && typeof handler.remove === 'function') {
            try { handler.remove(); } catch(e) { console.warn("Error removing handler:", e); }
          }
        });
        setExtraHandlers([]);

        // Restore navigation if possible
        if (mapView?.navigation && originalNavState) {
          try {
            mapView.navigation.browserTouchPanEnabled = originalNavState.browserTouchPanEnabled;
            if (originalNavState.keyboardNavigation !== undefined) {
                mapView.navigation.keyboardNavigation = originalNavState.keyboardNavigation;
            }
          } catch (e) { console.warn("Error restoring navigation:", e); }
        }
        setOriginalNavState(null);

        // Restore cursor
        if (mapView?.container) {
          try { mapView.container.style.cursor = 'default'; } catch(e) { console.warn("Error restoring cursor:", e); }
        }
      }
    };
  }, [isMultiDragMode, extraHandlers, originalNavState, mapView, localLabelDragger]);

  // --- Cleanup highlights when component closes or tab changes ---
  useEffect(() => {
    // Cleanup function runs when component unmounts or isOpen changes to false
    return () => {
      if (!isOpen) {
        console.log("[LabelEditor] Component closing - cleaning up highlights");

        // Get the dragger from any available source
        const dragger = localLabelDragger ||
                       (typeof window !== 'undefined' ? window.labelDragger : null);

        // Remove highlight boxes
        if (dragger && typeof dragger._removeHighlights === 'function') {
          try {
            dragger._removeHighlights();
            console.log("[LabelEditor] Highlight boxes removed on component close");
          } catch (e) {
            console.warn("[LabelEditor] Error removing highlights on close:", e);
          }
        }

        // Deselect any selected label
        if (labelManager) {
          labelManager.selectedLabel = null;
        }
        setSelectedLabel(null);
      }
    };
  }, [isOpen, localLabelDragger, labelManager]);

  // UPDATED: Enhanced updateFontSize function that ensures fontSize is saved properly
  const updateFontSize = (newSize) => {
    setFontSize(newSize); // Update local state immediately
    setHasUnsavedChanges(true); // Mark as changed

    if (!labelManager || typeof labelManager.updateLabelFontSize !== 'function') {
        console.warn("Cannot update font size: Manager or method missing.");
        return;
    }

    try {
        if (selectAllMode) {
            if (typeof labelManager.getAllLabelGraphics === 'function') {
                const allLabels = labelManager.getAllLabelGraphics();
                let updateCount = 0;
                allLabels.forEach(label => {
                    if (label && label.symbol) {
                        // Update the actual graphic
                        labelManager.updateLabelFontSize(label, newSize);
                        
                        // CRITICAL: Also update in the editedLabels map with explicit fontSize property
                        const labelId = labelManager.getLabelId(label);
                        if (labelId && labelManager.editedLabels) {
                            const existingData = labelManager.editedLabels.get(labelId) || {};
                            labelManager.editedLabels.set(labelId, {
                                ...existingData,
                                fontSize: newSize, // Store fontSize DIRECTLY in the data object
                                graphic: label
                            });
                        }
                        
                        updateCount++;
                    }
                });
                console.log(`[LabelEditor] Updated font size to ${newSize} for ALL labels (${updateCount} updates)`);
                setStatusMessage({ type: 'success', text: `Updated font size to ${newSize}px for ${updateCount} labels`, timeout: 3000 });
                
                // CRITICAL: Save immediately after updating all labels to ensure persistence
                if (typeof labelManager.savePositions === 'function') {
                    labelManager.savePositions(true); // Force immediate save
                }
            } else {
                console.warn("[LabelEditor] Cannot get all labels: getAllLabelGraphics method missing");
                setStatusMessage({ type: 'warning', text: `Cannot update all labels: method missing`, timeout: 3000 });
            }
        } else if (selectedLabel) {
            // Update the actual graphic
            labelManager.updateLabelFontSize(selectedLabel, newSize);
            
            // CRITICAL: Also update in the editedLabels map with explicit fontSize property
            const labelId = labelManager.getLabelId(selectedLabel);
            if (labelId && labelManager.editedLabels) {
                const existingData = labelManager.editedLabels.get(labelId) || {};
                labelManager.editedLabels.set(labelId, {
                    ...existingData,
                    fontSize: newSize, // Store fontSize DIRECTLY in the data object
                    graphic: selectedLabel
                });
            }
            
            console.log(`[LabelEditor] Updated font size to ${newSize} for selected label`);
            
            // CRITICAL: Save immediately to ensure persistence
            if (typeof labelManager.savePositions === 'function') {
                labelManager.savePositions(true); // Force immediate save
            }
        }
    } catch (error) {
        console.error('[LabelEditor] Error updating font size:', error);
        setStatusMessage({ type: 'error', text: `Font size update error: ${error.message}`, timeout: 5000 });
    }
  };

  // --- Update Background Opacity for Selected Label ---
  const updateBackgroundOpacity = (opacity) => {
    setBackgroundOpacity(opacity);
    setHasUnsavedChanges(true); // Mark as changed
    
    if (!labelManager || !selectedLabel || !selectedLabel.symbol) {
      console.warn("[LabelEditor] Cannot update background opacity: Missing required references");
      return;
    }
    
    try {
      // Clone the symbol for modification
      const newSymbol = selectedLabel.symbol.clone();
      
      // Get current background color
      let bgColor = newSymbol.backgroundColor;
      
      // If not already using background or array format, create default white
      if (!bgColor || !Array.isArray(bgColor)) {
        bgColor = [255, 255, 255, opacity];
      } else {
        // If array format, update the opacity (alpha) value
        bgColor[3] = opacity;
      }
      
      // Apply the updated background color
      newSymbol.backgroundColor = bgColor;

      // CRITICAL: Force ArcGIS to recognize the change by removing and re-adding the graphic
      const parentLayer = selectedLabel.layer || labelManager.labelLayer;
      if (parentLayer && parentLayer.graphics) {
        parentLayer.graphics.remove(selectedLabel);
        selectedLabel.symbol = newSymbol;
        parentLayer.graphics.add(selectedLabel);
        console.log(`[LabelEditor] Updated background opacity to ${opacity} (removed and re-added)`);
      } else {
        selectedLabel.symbol = newSymbol;
        console.log(`[LabelEditor] Updated background opacity to ${opacity} (direct assignment)`);
      }

      // Update in label manager if it has tracking - CRITICAL: Preserve position
      if (typeof labelManager.getLabelId === 'function' &&
          typeof labelManager.editedLabels === 'object' &&
          labelManager.editedLabels) {
        const labelId = labelManager.getLabelId(selectedLabel);
        if (labelId) {
          const existingData = labelManager.editedLabels.get(labelId) || {};

          // Preserve current position from geometry if not already stored
          let currentLat = existingData.latitude;
          let currentLon = existingData.longitude;
          if (currentLat === undefined && selectedLabel.geometry) {
            currentLat = selectedLabel.geometry.latitude ?? selectedLabel.geometry.y;
            currentLon = selectedLabel.geometry.longitude ?? selectedLabel.geometry.x;
          }

          // Get background properties from symbol to ensure they're saved
          const bgColor = newSymbol.backgroundColor || existingData.backgroundColor;
          const borderColor = newSymbol.borderLineColor || existingData.borderLineColor;
          const borderSize = newSymbol.borderLineSize ?? existingData.borderLineSize ?? 0.1;

          labelManager.editedLabels.set(labelId, {
            ...existingData,
            graphic: selectedLabel,
            latitude: currentLat,
            longitude: currentLon,
            manuallyPositioned: existingData.manuallyPositioned || false,
            backgroundColor: bgColor,
            borderLineColor: borderColor,
            borderLineSize: borderSize
          });
        }
      }

      // Save changes
      if (typeof labelManager.savePositions === 'function') {
        labelManager.savePositions(true);
      }
    } catch (error) {
      console.error("[LabelEditor] Error updating background opacity:", error);
      setStatusMessage({
        type: 'error',
        text: `Failed to update background opacity: ${error.message}`,
        timeout: 3000
      });
    }
  };

  // --- Update Background Opacity for All Labels ---
  const updateGlobalBackgroundOpacity = (opacity) => {
    setBackgroundOpacity(opacity); // Update state
    setHasUnsavedChanges(true); // Mark as changed
    
    if (!labelManager || typeof labelManager.getAllLabelGraphics !== 'function') {
      console.warn("[LabelEditor] Cannot update global background opacity: Missing required methods");
      return;
    }
    
    try {
      const allLabels = labelManager.getAllLabelGraphics();
      if (!allLabels || allLabels.length === 0) return;
      
      let updateCount = 0;
      
      allLabels.forEach(label => {
        if (!label || !label.symbol) return;
        
        // Only update labels that already have a background
        if (hasBackgroundRectangle(label.symbol)) {
          const newSymbol = label.symbol.clone();
          let bgColor = newSymbol.backgroundColor;

          if (Array.isArray(bgColor)) {
            bgColor[3] = opacity;
          } else if (bgColor && typeof bgColor === 'object') {
            bgColor.a = opacity;
          } else {
            bgColor = [255, 255, 255, opacity];
          }

          newSymbol.backgroundColor = bgColor;

          // CRITICAL: Force ArcGIS to recognize the change by removing and re-adding the graphic
          const parentLayer = label.layer || labelManager.labelLayer;
          if (parentLayer && parentLayer.graphics) {
            parentLayer.graphics.remove(label);
            label.symbol = newSymbol;
            parentLayer.graphics.add(label);
          } else {
            label.symbol = newSymbol;
          }

          // Track in manager's editedLabels if available - CRITICAL: Preserve position
          if (typeof labelManager.getLabelId === 'function' &&
              typeof labelManager.editedLabels === 'object' &&
              labelManager.editedLabels) {
            const labelId = labelManager.getLabelId(label);
            if (labelId) {
              const existingData = labelManager.editedLabels.get(labelId) || {};

              // Preserve current position from geometry if not already stored
              let currentLat = existingData.latitude;
              let currentLon = existingData.longitude;
              if (currentLat === undefined && label.geometry) {
                currentLat = label.geometry.latitude ?? label.geometry.y;
                currentLon = label.geometry.longitude ?? label.geometry.x;
              }

                // Get background colors from symbol to ensure they're saved
              const bgColor = newSymbol.backgroundColor || existingData.backgroundColor;
              const borderColor = newSymbol.borderLineColor || existingData.borderLineColor;
              const borderSz = newSymbol.borderLineSize ?? existingData.borderLineSize ?? 0.1;

              labelManager.editedLabels.set(labelId, {
                ...existingData,
                graphic: label,
                latitude: currentLat,
                longitude: currentLon,
                manuallyPositioned: existingData.manuallyPositioned || false,
                backgroundColor: bgColor,
                borderLineColor: borderColor,
                borderLineSize: borderSz
              });
            }
          }

          updateCount++;
        }
      });

      // Save changes
      if (typeof labelManager.savePositions === 'function') {
        labelManager.savePositions(true);
      }

      if (updateCount > 0) {
        console.log(`[LabelEditor] Updated background opacity to ${opacity} for ${updateCount} labels`);
        setStatusMessage({
          type: 'success',
          text: `Updated background opacity for ${updateCount} labels`,
          timeout: 2000
        });
      }
    } catch (error) {
      console.error("[LabelEditor] Error updating global background opacity:", error);
      setStatusMessage({
        type: 'error',
        text: `Failed to update background opacity: ${error.message}`,
        timeout: 3000
      });
    }
  };

  // --- Update Border Size for Selected Label ---
  const updateBorderSize = (size) => {
    setBorderSize(size);
    setHasUnsavedChanges(true); // Mark as changed

    if (!labelManager || !selectedLabel || !selectedLabel.symbol) {
      console.warn("[LabelEditor] Cannot update border size: Missing required references");
      return;
    }

    try {
      // Clone the symbol for modification
      const newSymbol = selectedLabel.symbol.clone();

      console.log(`[LabelEditor] updateBorderSize called with size=${size}`);
      console.log(`[LabelEditor] Symbol backgroundColor:`, newSymbol.backgroundColor);
      console.log(`[LabelEditor] hasBackgroundRectangle:`, hasBackgroundRectangle(newSymbol));

      // Only update if background is already enabled
      if (hasBackgroundRectangle(newSymbol)) {
        newSymbol.borderLineSize = size;
        // Ensure border color matches background (white) so it appears as padding
        const bgOpacity = newSymbol.backgroundColor?.[3] ?? backgroundOpacity;
        newSymbol.borderLineColor = [255, 255, 255, bgOpacity];
        console.log(`[LabelEditor] Setting borderLineSize to ${size} with white border color`);

        // CRITICAL: Force ArcGIS to recognize the change by removing and re-adding the graphic
        const parentLayer = selectedLabel.layer || labelManager.labelLayer;
        if (parentLayer && parentLayer.graphics) {
          parentLayer.graphics.remove(selectedLabel);
          selectedLabel.symbol = newSymbol;
          parentLayer.graphics.add(selectedLabel);
          console.log(`[LabelEditor] Updated border size to ${size} (removed and re-added)`);
        } else {
          selectedLabel.symbol = newSymbol;
          console.log(`[LabelEditor] Updated border size to ${size} (direct assignment)`);
        }

        // CRITICAL: Preserve position when updating border size
        const labelId = labelManager.getLabelId(selectedLabel);
        if (labelId && labelManager.editedLabels) {
          const existingData = labelManager.editedLabels.get(labelId) || {};

          // Preserve current position from geometry if not already stored
          let currentLat = existingData.latitude;
          let currentLon = existingData.longitude;
          if (currentLat === undefined && selectedLabel.geometry) {
            currentLat = selectedLabel.geometry.latitude ?? selectedLabel.geometry.y;
            currentLon = selectedLabel.geometry.longitude ?? selectedLabel.geometry.x;
          }

          // Get background colors from symbol to ensure they're saved
          const bgColor = newSymbol.backgroundColor || existingData.backgroundColor;
          const borderColor = newSymbol.borderLineColor || existingData.borderLineColor;

          labelManager.editedLabels.set(labelId, {
            ...existingData,
            graphic: selectedLabel,
            latitude: currentLat,
            longitude: currentLon,
            manuallyPositioned: existingData.manuallyPositioned || false,
            backgroundColor: bgColor,
            borderLineColor: borderColor,
            borderLineSize: size
          });
        }

        // Save changes
        if (typeof labelManager.savePositions === 'function') {
          labelManager.savePositions(true);
        }
      } else {
        console.warn(`[LabelEditor] Cannot update border size: No background rectangle detected`);
      }
    } catch (error) {
      console.error("[LabelEditor] Error updating border size:", error);
      setStatusMessage({
        type: 'error',
        text: `Failed to update border size: ${error.message}`,
        timeout: 3000
      });
    }
  };

  // --- Update Border Size for All Labels ---
  const updateGlobalBorderSize = (size) => {
    setBorderSize(size); // Update state
    setHasUnsavedChanges(true); // Mark as changed

    if (!labelManager || typeof labelManager.getAllLabelGraphics !== 'function') {
      console.warn("[LabelEditor] Cannot update global border size: Missing required methods");
      return;
    }

    try {
      const allLabels = labelManager.getAllLabelGraphics();
      if (!allLabels || allLabels.length === 0) return;

      let updateCount = 0;

      allLabels.forEach(label => {
        if (!label || !label.symbol) return;

        // Only update labels that already have a background
        if (hasBackgroundRectangle(label.symbol)) {
          const newSymbol = label.symbol.clone();
          newSymbol.borderLineSize = size;
          // Ensure border color matches background (white) so it appears as padding
          const bgOpacity = newSymbol.backgroundColor?.[3] ?? backgroundOpacity;
          newSymbol.borderLineColor = [255, 255, 255, bgOpacity];

          // CRITICAL: Force ArcGIS to recognize the change by removing and re-adding the graphic
          const parentLayer = label.layer || labelManager.labelLayer;
          if (parentLayer && parentLayer.graphics) {
            parentLayer.graphics.remove(label);
            label.symbol = newSymbol;
            parentLayer.graphics.add(label);
          } else {
            label.symbol = newSymbol;
          }

          // CRITICAL: Preserve position when updating border size
          const labelId = labelManager.getLabelId(label);
          if (labelId && labelManager.editedLabels) {
            const existingData = labelManager.editedLabels.get(labelId) || {};

            // Preserve current position from geometry if not already stored
            let currentLat = existingData.latitude;
            let currentLon = existingData.longitude;
            if (currentLat === undefined && label.geometry) {
              currentLat = label.geometry.latitude ?? label.geometry.y;
              currentLon = label.geometry.longitude ?? label.geometry.x;
            }

            // Get background colors from symbol to ensure they're saved
            const bgColor = newSymbol.backgroundColor || existingData.backgroundColor;
            const borderColor = newSymbol.borderLineColor || existingData.borderLineColor;

            labelManager.editedLabels.set(labelId, {
              ...existingData,
              graphic: label,
              latitude: currentLat,
              longitude: currentLon,
              manuallyPositioned: existingData.manuallyPositioned || false,
              backgroundColor: bgColor,
              borderLineColor: borderColor,
              borderLineSize: size
            });
          }

          updateCount++;
        }
      });

      // Save changes
      if (typeof labelManager.savePositions === 'function') {
        labelManager.savePositions(true);
      }

      if (updateCount > 0) {
        console.log(`[LabelEditor] Updated border size to ${size} for ${updateCount} labels`);
        setStatusMessage({
          type: 'success',
          text: `Updated border size for ${updateCount} labels`,
          timeout: 2000
        });
      }
    } catch (error) {
      console.error("[LabelEditor] Error updating global border size:", error);
      setStatusMessage({
        type: 'error',
        text: `Failed to update border size: ${error.message}`,
        timeout: 3000
      });
    }
  };

  // --- Find Data Point Graphic Associated with a Label ---
  const findDataPointForLabel = (labelGraphic) => {
    if (!labelGraphic || !mapView?.map?.layers) return null;

    const parentID = labelGraphic.attributes?.parentID;
    const dataPointGeometry = labelGraphic.attributes?.dataPointGeometry;

    // Search all graphics layers
    for (const layer of mapView.map.layers) {
      if (!layer.graphics) continue;

      for (const graphic of layer.graphics) {
        // Skip labels (text symbols)
        if (graphic.attributes?.isLabel || graphic.symbol?.type === "text") {
          continue;
        }

        // Match by parentID -> OBJECTID
        if (parentID !== undefined && parentID !== null) {
          const graphicOBJECTID = graphic.attributes?.OBJECTID;
          if (graphicOBJECTID !== undefined && graphicOBJECTID === parentID) {
            return graphic;
          }
        }

        // Match by geometry (if dataPointGeometry is stored)
        if (dataPointGeometry && graphic.geometry) {
          const dpLat = dataPointGeometry.latitude ?? dataPointGeometry.y;
          const dpLon = dataPointGeometry.longitude ?? dataPointGeometry.x;
          const gLat = graphic.geometry.latitude ?? graphic.geometry.y;
          const gLon = graphic.geometry.longitude ?? graphic.geometry.x;

          if (dpLat !== undefined && dpLon !== undefined &&
              gLat !== undefined && gLon !== undefined) {
            const dLat = gLat - dpLat;
            const dLon = gLon - dpLon;
            const dist = Math.sqrt(dLat * dLat + dLon * dLon);
            if (dist < 0.0001) { // Very close match
              return graphic;
            }
          }
        }
      }
    }

    return null;
  };

  // --- Update Data Point Shape for Selected Label ---
  const updateDataPointShape = (newShape) => {
    setDataPointShape(newShape);
    setHasUnsavedChanges(true);

    if (!selectedLabel) {
      console.warn("[LabelEditor] Cannot update data point shape: No label selected");
      return;
    }

    try {
      const dataPoint = findDataPointForLabel(selectedLabel);
      if (!dataPoint) {
        console.warn("[LabelEditor] Cannot update data point shape: Data point not found for label");
        setStatusMessage({
          type: 'warning',
          text: 'Could not find data point for this label',
          timeout: 3000
        });
        return;
      }

      // Clone and modify the symbol
      if (dataPoint.symbol && dataPoint.symbol.clone) {
        const newSymbol = dataPoint.symbol.clone();
        newSymbol.style = newShape;
        dataPoint.symbol = newSymbol;

        // Force graphics layer refresh if available
        if (dataPoint.layer && typeof dataPoint.layer.refresh === 'function') {
          dataPoint.layer.refresh();
        }
        // Note: Setting the symbol directly on the graphic auto-updates the display in ArcGIS

        console.log(`[LabelEditor] Updated data point shape to "${newShape}"`);
        setStatusMessage({
          type: 'success',
          text: `Changed point shape to ${newShape}`,
          timeout: 2000
        });
      } else {
        console.warn("[LabelEditor] Data point has no clonable symbol");
      }
    } catch (error) {
      console.error("[LabelEditor] Error updating data point shape:", error);
      setStatusMessage({
        type: 'error',
        text: `Failed to update point shape: ${error.message}`,
        timeout: 3000
      });
    }
  };

  // --- Get Current Data Point Shape for Selected Label ---
  const getCurrentDataPointShape = () => {
    if (!selectedLabel) return 'circle';

    const dataPoint = findDataPointForLabel(selectedLabel);
    if (dataPoint?.symbol?.style) {
      return dataPoint.symbol.style;
    }
    return 'circle';
  };

  const togglePositionAdjustmentMode = (enable) => {
    const newMode = enable !== undefined ? enable : !isMultiDragMode;
    console.log(`[PositionAdjust] Toggling mode to: ${newMode}`);
    
    // Update state first
    setIsMultiDragMode(newMode);
    
    try {
      // Store original navigation state before disabling
      if (newMode && mapView && mapView.navigation) {
        setOriginalNavState({
          browserTouchPanEnabled: mapView.navigation.browserTouchPanEnabled,
          mouseWheelZoomEnabled: mapView.navigation.mouseWheelZoomEnabled,
          keyboardNavigation: mapView.navigation.keyboardNavigation,
          momentumEnabled: mapView.navigation.momentumEnabled || false,
          dragEnabled: mapView.navigation.dragEnabled || true
        });
        
        // CRITICAL FIX: Completely disable map navigation during dragging
        mapView.navigation.browserTouchPanEnabled = false;
        mapView.navigation.mouseWheelZoomEnabled = false;
        mapView.navigation.keyboardNavigation = false;
        mapView.navigation.momentumEnabled = false;
        
        // Disable drag navigation if that property exists
        if (typeof mapView.navigation.dragEnabled !== 'undefined') {
          mapView.navigation.dragEnabled = false;
        }
        
        // Set a flag on the mapView to indicate label editing mode
        mapView._labelEditingActive = true;
        
        // Add class to map container to prevent default touch behaviors
        if (mapView.container) {
          mapView.container.classList.add('label-edit-mode');
        }
      }
      
      // Try to get the dragger from our state or window
      let dragger = localLabelDragger || 
                   (typeof window !== 'undefined' ? window.labelDragger : null);
      
      // If we don't have a dragger yet, create one
      if (!dragger && newMode && mapView && labelManager) {
        console.log("[PositionAdjust] Creating a new SimpleLabelDragger instance");
        
        try {
          const newDragger = new SimpleLabelDragger({
            view: mapView,
            labelManager: labelManager,
            onDragStart: (label) => {
              console.log("[LabelDragger] Started dragging label:", labelManager.getLabelId(label));
              setHasUnsavedChanges(true); // Mark as changed
            },
            onDrag: (label, dx, dy) => {
              console.log("[LabelDragger] Dragging label:", labelManager.getLabelId(label), `delta: (${dx}, ${dy})`);
            },
            onDragEnd: (label) => {
              console.log("[LabelDragger] Finished dragging label:", labelManager.getLabelId(label));
              setHasUnsavedChanges(true); // Mark as changed
              // Save after drag ends
              if (labelManager && typeof labelManager.savePositions === 'function') {
                labelManager.savePositions(true).catch(err => {
                  console.error("[LabelDragger] Error saving after drag:", err);
                });
              }
            }
          });
          
          // Initialize the dragger
          if (typeof newDragger.initialize === 'function') {
            newDragger.initialize();
          }
          
          // Store the new dragger
          dragger = newDragger;
          setLocalLabelDragger(newDragger);
          window.labelDragger = newDragger;
          setDraggerInitialized(true);
          
          console.log("[PositionAdjust] Successfully created SimpleLabelDragger");
        } catch (initError) {
          console.error("[PositionAdjust] Error creating dragger:", initError);
          throw new Error("Failed to create label dragger: " + initError.message);
        }
      }
      
      // Verify we have a usable dragger
      if (!dragger) {
        throw new Error("No dragger available and could not create one");
      }
      
      // Check if dragger has the necessary methods
      const hasEnableMethod = typeof dragger.enable === 'function';
      const hasActivateMethod = typeof dragger.activate === 'function';
      const hasDeactivateMethod = typeof dragger.deactivate === 'function';
      
      if (!hasEnableMethod && !hasActivateMethod && !hasDeactivateMethod) {
        throw new Error("Dragger is missing required methods: enable, activate, or deactivate");
      }
      
      // Toggle dragger state using the available method
      if (newMode) {
        // Activate dragger using the most appropriate method
        if (hasActivateMethod) {
          const result = dragger.activate();
          console.log("[PositionAdjust] Dragger activate result:", result);
        } else if (hasEnableMethod) {
          const result = dragger.enable(true);
          console.log("[PositionAdjust] Dragger enable(true) result:", result);
        } else {
          throw new Error("Cannot activate dragger - no suitable method available");
        }
      } else {
        // Deactivate dragger using the most appropriate method
        if (hasDeactivateMethod) {
          const result = dragger.deactivate();
          console.log("[PositionAdjust] Dragger deactivate result:", result);
        } else if (hasEnableMethod) {
          const result = dragger.enable(false);
          console.log("[PositionAdjust] Dragger enable(false) result:", result);
        } else {
          throw new Error("Cannot deactivate dragger - no suitable method available");
        }
        
        // Restore map navigation when deactivating
        if (mapView && mapView.navigation && originalNavState) {
          mapView.navigation.browserTouchPanEnabled = originalNavState.browserTouchPanEnabled;
          mapView.navigation.mouseWheelZoomEnabled = originalNavState.mouseWheelZoomEnabled;
          mapView.navigation.keyboardNavigation = originalNavState.keyboardNavigation || false;
          mapView.navigation.momentumEnabled = originalNavState.momentumEnabled || false;
          
          // Restore drag navigation if applicable
          if (typeof mapView.navigation.dragEnabled !== 'undefined') {
            mapView.navigation.dragEnabled = originalNavState.dragEnabled || true;
          }
          
          // Remove the editing flag
          mapView._labelEditingActive = false;
        }
        
        // Remove class from map container
        if (mapView && mapView.container) {
          mapView.container.classList.remove('label-edit-mode');
        }
      }
      
      // Update map cursor to indicate mode
      if (mapView && mapView.container) {
        mapView.container.style.cursor = newMode ? 'move' : 'default';
      }
      
      setStatusMessage({ 
        type: 'info', 
        text: newMode ? 'Position Adjustment Mode: Click to select a label, then click again to place it' : 'Position adjustment mode disabled', 
        timeout: 3000 
      });
      
      return true;
    } catch (error) {
      console.error("[PositionAdjust] Error toggling dragger:", error);
      setStatusMessage({ 
        type: 'error', 
        text: `Error toggling position adjustment mode: ${error.message}`, 
        timeout: 5000 
      });
      
      // Reset navigation and UI state on error
      if (newMode && mapView && mapView.navigation && originalNavState) {
        mapView.navigation.browserTouchPanEnabled = originalNavState.browserTouchPanEnabled;
        mapView.navigation.mouseWheelZoomEnabled = originalNavState.mouseWheelZoomEnabled;
        mapView.navigation.keyboardNavigation = originalNavState.keyboardNavigation || false;
        mapView.navigation.momentumEnabled = originalNavState.momentumEnabled || false;
        mapView._labelEditingActive = false;
      }
      
      // Reset UI state
      setIsMultiDragMode(false);
      
      return false;
    }
  };

  // --- Update Label Text ---
  const updateLabelText = (newText) => {
    const cleanText = newText.trim();
    setLabelText(cleanText); // Update local state for input field
    setHasUnsavedChanges(true); // Mark as changed

    if (!labelManager || typeof labelManager.updateLabelText !== 'function' || !selectedLabel) {
        console.warn("Cannot update label text: Manager, method, or selected label missing.");
        return;
    }

    try {
        labelManager.updateLabelText(selectedLabel, cleanText); // Pass the clean base text
        console.log(`[LabelEditor] Updated text for selected label to: "${cleanText}"`);
        // UI display text is handled by watchSelection reading symbol.text
    } catch (error) {
        console.error('[LabelEditor] Error updating label text:', error);
        setStatusMessage({ type: 'error', text: `Text update error: ${error.message}`, timeout: 5000 });
    }
  };

  // --- Enable Single Label Moving Mode (DEPRECATED - use direct drag) ---
  const enableMovingMode = () => {
    console.warn("[LabelEditor] enableMovingMode is deprecated. Direct drag is handled in Map.jsx.");
    // If you need a fallback or specific UI state:
    // setIsMoving(true); // Set UI state if needed for feedback
    setStatusMessage({ type: 'info', text: 'Drag label directly on the map to adjust position.', timeout: 4000 });
  };

  // --- Stop Single Label Moving Mode (DEPRECATED) ---
  const stopMovingMode = () => {
    console.warn("[LabelEditor] stopMovingMode is deprecated.");
    // setIsMoving(false); // Reset UI state if used
  };

  // UPDATED: updateLabelOption function to properly track all changes
  const updateLabelOption = (option, value) => {
    console.log(`[LabelEditor] Updating ${option} to ${value}`);
    setLabelOptions(prev => ({ ...prev, [option]: value })); // Update general UI state
    setHasUnsavedChanges(true); // Mark as changed

    if (!labelManager || !labelManager.getLabelId) {
        console.warn("[LabelEditor] No label manager or required methods available to apply changes");
        return;
    }

    try {
        const applyChange = (labelGraphic) => {
            if (!labelGraphic || !labelGraphic.symbol) return false;
            const newSymbol = labelGraphic.symbol.clone();
            let changed = false;

            switch (option) {
                case 'bold':
                    if (!newSymbol.font) newSymbol.font = {};
                    const newWeight = value ? 'bold' : 'normal';
                    if (newSymbol.font.weight !== newWeight) {
                        newSymbol.font.weight = newWeight;
                        changed = true;
                    }
                    break;

                case 'whiteBackground':
                    const currentBg = hasBackgroundRectangle(newSymbol);
                    if (value !== currentBg) {
                        if (value) {
                            // When enabling background, use standard size (4) as default
                            // Border color matches background (white) so borderLineSize acts as padding
                            const defaultSize = 4; // Standard size
                            setBorderSize(defaultSize);
                            newSymbol.backgroundColor = [255, 255, 255, backgroundOpacity];
                            newSymbol.borderLineColor = [255, 255, 255, backgroundOpacity];
                            newSymbol.borderLineSize = defaultSize;
                            newSymbol.haloSize = 0; newSymbol.haloColor = null; // Remove halo
                        } else {
                            newSymbol.backgroundColor = null;
                            newSymbol.borderLineColor = null;
                            newSymbol.borderLineSize = 0;
                        }
                        changed = true;
                    }
                    break;
            }

            if (changed) {
                labelGraphic.symbol = newSymbol; // Apply the updated symbol
                const labelId = labelManager.getLabelId(labelGraphic);
                if (labelId && labelManager.editedLabels) {
                    const existingData = labelManager.editedLabels.get(labelId) || {};

                    // CRITICAL: Preserve the current position when updating style options
                    // Get current position from the graphic's geometry
                    let currentLat = existingData.latitude;
                    let currentLon = existingData.longitude;

                    // If not already stored, get from the graphic's current geometry
                    if (currentLat === undefined && labelGraphic.geometry) {
                        currentLat = labelGraphic.geometry.latitude ?? labelGraphic.geometry.y;
                        currentLon = labelGraphic.geometry.longitude ?? labelGraphic.geometry.x;
                    }

                    // Store BOTH in the symbol AND in the editedLabels data to ensure persistence
                    labelManager.editedLabels.set(labelId, {
                        ...existingData,
                        graphic: labelGraphic,
                        // CRITICAL: Preserve position coordinates
                        latitude: currentLat,
                        longitude: currentLon,
                        manuallyPositioned: existingData.manuallyPositioned || false,
                        // IMPORTANT: Explicitly store the font weight and background settings
                        fontWeight: option === 'bold' ? (value ? 'bold' : 'normal') : existingData.fontWeight,
                        backgroundColor: option === 'whiteBackground' ?
                            (value ? [255, 255, 255, backgroundOpacity] : null) :
                            existingData.backgroundColor,
                        borderLineColor: option === 'whiteBackground' ?
                            (value ? [200, 200, 200, 0.5] : null) :
                            existingData.borderLineColor,
                        borderLineSize: option === 'whiteBackground' && value ? borderSize : existingData.borderLineSize
                    });
                }
                return true;
            }
            return false;
        };

        let updateCount = 0;
        let refreshNeeded = false;

        if (selectAllMode) {
            const allLabels = labelManager.getAllLabelGraphics ? labelManager.getAllLabelGraphics() : [];
            if (allLabels.length === 0) return;
            allLabels.forEach(label => {
                if (applyChange(label)) { updateCount++; refreshNeeded = true; }
            });
            if (updateCount > 0) {
                console.log(`[LabelEditor] Updated ${option} to ${value} for ${updateCount} labels`);
                setStatusMessage({ type: 'success', text: `Updated ${option} for ${updateCount} labels`, timeout: 2000 });
            }
        } else if (selectedLabel) {
            if (applyChange(selectedLabel)) {
                updateCount++; refreshNeeded = true;
                console.log(`[LabelEditor] Updated ${option} to ${value} for selected label`);
                // Sync UI toggle state after successful change
                const updatedSymbol = selectedLabel.symbol;
                if (option === 'bold') setLabelOptions(prev => ({ ...prev, bold: updatedSymbol?.font?.weight === 'bold' }));
                if (option === 'whiteBackground') setLabelOptions(prev => ({ ...prev, whiteBackground: hasBackgroundRectangle(updatedSymbol) }));
            }
        } else {
            console.warn("[LabelEditor] No label selected and not in Select All mode"); return;
        }

        if (refreshNeeded) {
            // CRITICAL: Save positions FIRST to preserve manually adjusted positions
            // before refreshLabels() reads from localStorage
            labelManager.savePositions(true);

            // Then refresh to update the display
            if (labelManager.refreshLabels) labelManager.refreshLabels();
            else if (mapView?.graphics) mapView.graphics.refresh();
        }

    } catch (error) {
        console.error(`[LabelEditor] Error updating ${option}:`, error);
        setStatusMessage({ type: 'error', text: `Failed to update ${option}: ${error.message}`, timeout: 3000 });
        setLabelOptions(prev => ({ ...prev, [option]: !value })); // Revert UI toggle
    }
  };

  // --- Toggle Select All Mode ---
  const toggleSelectAllMode = () => {
    const nextMode = !selectAllMode;
    setSelectAllMode(nextMode);
    setHasUnsavedChanges(true); // Mark as changed

    if (nextMode) {
        if (selectedLabel) { // Deselect single label if activating Select All
             labelManager.selectedLabel = null;
             setSelectedLabel(null);
        }
        // if (isMoving) stopMovingMode(); // isMoving deprecated
        if (isMultiDragMode) togglePositionAdjustmentMode(false); // Turn off position adjustment

        if (labelManager && typeof labelManager.getAllLabelGraphics === 'function') {
            const allLabels = labelManager.getAllLabelGraphics();
            setAllLabelsCount(allLabels.length || 0);
            if (allLabels.length > 0 && allLabels[0].symbol) {
                const firstSymbol = allLabels[0].symbol;
                setFontSize(firstSymbol.font?.size || 10);
                setLabelOptions(prev => ({
                    ...prev,
                    bold: firstSymbol.font?.weight === 'bold',
                    whiteBackground: hasBackgroundRectangle(firstSymbol)
                }));
                
                // Set background opacity and border size from first label with background
                const labelWithBg = allLabels.find(l => l && l.symbol && hasBackgroundRectangle(l.symbol));
                if (labelWithBg && labelWithBg.symbol) {
                  setBackgroundOpacity(getBackgroundOpacity(labelWithBg.symbol));
                  setBorderSize(getBackgroundBorderSize(labelWithBg.symbol));
                }
            } else {
                setFontSize(10);
                setLabelOptions(prev => ({ ...prev, bold: false, whiteBackground: false }));
                setBackgroundOpacity(0.85);
                setBorderSize(0.5);
            }
        } else {
            setFontSize(10);
            setLabelOptions(prev => ({ ...prev, bold: false, whiteBackground: false }));
            setBackgroundOpacity(0.85);
            setBorderSize(0.5);
        }
        setStatusMessage({ type: 'info', text: `Select All Mode: Affects ${allLabelsCount} labels.`, timeout: 3000 });
    } else {
        setFontSize(10);
        setLabelOptions(prev => ({ ...prev, bold: false, whiteBackground: false }));
        setBackgroundOpacity(0.85);
        setBorderSize(0.5);
    }
    console.log(`[LabelEditor] Toggled 'Select All' mode to: ${nextMode}`);
  };

  // UPDATED: Enhanced finalizeLabels function with more robust saving (now private)
  const finalizeLabels = async (silent = false) => {
    if (isSaving || !labelManager || typeof labelManager.savePositions !== 'function') {
        console.log(`[LabelEditor] Save skipped: ${isSaving ? 'Already saving' : 'Manager/method missing'}`);
        return false;
    }
    setIsSaving(true);

    try {
        // Commit pending UI changes first
        if (isTextEditing && selectedLabel) {
            const baseText = selectedLabel.attributes?.hasCustomFormat
                ? labelText.replace(/\s*\(.*\)$/, '').trim()
                : labelText;
            updateLabelText(baseText);
            setIsTextEditing(false);
        }

        // CRITICAL: Make sure we capture and save current UI state for fontSize 
        // and other properties before finalizing
        if (selectAllMode) {
            // In select all mode, apply current fontSize to all labels
            const allLabels = labelManager.getAllLabelGraphics ? labelManager.getAllLabelGraphics() : [];
            if (allLabels && allLabels.length > 0) {
                console.log(`[LabelEditor] Applying final fontSize ${fontSize} to all ${allLabels.length} labels`);
                allLabels.forEach(label => {
                    if (label && typeof labelManager.updateLabelFontSize === 'function') {
                        // Force update fontSize in the graphic
                        labelManager.updateLabelFontSize(label, fontSize);
                        
                        // Also ensure it's stored in editedLabels with all properties
                        const labelId = labelManager.getLabelId(label);
                        if (labelId && labelManager.editedLabels) {
                            const existingData = labelManager.editedLabels.get(labelId) || {};
                            labelManager.editedLabels.set(labelId, {
                                ...existingData,
                                fontSize: fontSize, // Store fontSize DIRECTLY in the data object
                                fontWeight: labelOptions.bold ? 'bold' : 'normal',
                                backgroundColor: labelOptions.whiteBackground ? [255, 255, 255, backgroundOpacity] : null
                            });
                        }
                    }
                });
            }
        } else if (selectedLabel) {
            // For single label, ensure current UI state is applied
            if (typeof labelManager.updateLabelFontSize === 'function') {
                labelManager.updateLabelFontSize(selectedLabel, fontSize);
            }
            
            // Force bold and background settings to be applied
            const labelId = labelManager.getLabelId(selectedLabel);
            if (labelId && labelManager.editedLabels) {
                const existingData = labelManager.editedLabels.get(labelId) || {};
                labelManager.editedLabels.set(labelId, {
                    ...existingData,
                    fontSize: fontSize, // Store fontSize DIRECTLY in the data object
                    fontWeight: labelOptions.bold ? 'bold' : 'normal',
                    backgroundColor: labelOptions.whiteBackground ? [255, 255, 255, backgroundOpacity] : null
                });
            }
        }

        // Force immediate save with true parameter
        console.log("[LabelEditor] Performing final save with current font settings");
        const result = await labelManager.savePositions(true); 
        console.log('[LabelEditor] Save result:', result);

        if (!silent) {
            const count = (labelManager.editedLabels?.size !== undefined) ? labelManager.editedLabels.size : (result?.count || 0);
            setStatusMessage({
                type: 'success',
                text: count > 0 ? `Applied ${count} label modifications.` : 'No modifications to apply.',
                timeout: 3000
            });
        }
        
        // After saving, force a refresh of all labels to make sure changes are visible
        if (typeof labelManager.refreshLabels === 'function') {
            setTimeout(() => labelManager.refreshLabels(), 100);
        }
        
        if (labelManager.editedLabels?.size) {
            console.log(`[LabelEditor] Successfully saved ${labelManager.editedLabels.size} edited labels.`);
        }
        return true;
    } catch (error) {
        console.error('[LabelEditor] Error saving labels:', error);
        if (!silent) {
            setStatusMessage({ type: 'error', text: `Save error: ${error.message}`, timeout: 5000 });
        }
        return false;
    } finally {
        setIsSaving(false);
    }
  };

  /**
   * Apply and save all changes with comprehensive error handling
   * Provides detailed user feedback and manages UI state appropriately
   */
  const applyChanges = async () => {
    try {
      setIsSaving(true);

      // Apply changes using existing finalizeLabels logic
      const success = await finalizeLabels(false); // Pass false to show messages

      if (success) {
        // Reset change tracking
        setHasUnsavedChanges(false);

        // Clear highlight boxes before closing
        const dragger = localLabelDragger || window.labelDragger || labelDragger;
        if (dragger && typeof dragger._removeHighlights === 'function') {
          console.log("[LabelEditor] Clearing highlight boxes on apply");
          dragger._removeHighlights();
        }

        // Prepare success message
        const count = (labelManager.editedLabels?.size !== undefined)
          ? labelManager.editedLabels.size
          : 0;
        const message = count > 0
          ? `Applied ${count} label modifications successfully`
          : 'No modifications to apply';

        // Show success message with shorter timeout since we're closing
        setStatusMessage({
          type: 'success',
          text: message,
          timeout: 2000
        });

        // Close the editor after a brief delay to show the success message
        setTimeout(() => {
          if (onClose) {
            console.log("[LabelEditor] Closing editor after successful save");
            onClose();
          }
        }, 1500);
      }
    } catch (error) {
      console.error("[LabelEditor] Failed to apply and save changes:", error);

      // Clear highlight boxes even on error
      const dragger = localLabelDragger || window.labelDragger || labelDragger;
      if (dragger && typeof dragger._removeHighlights === 'function') {
        dragger._removeHighlights();
      }

      // Provide user-friendly error messages
      let userMessage = "Failed to save label changes. ";

      if (error.message.includes('Manager')) {
        userMessage += "Label manager is not available. Your changes have been applied locally.";
      } else if (error.message.includes('localStorage')) {
        userMessage += "Unable to save to storage. Please try again.";
      } else {
        userMessage += "An unexpected error occurred. Your changes were applied but may not be saved.";
      }

      setStatusMessage({
        type: 'error',
        text: userMessage,
        timeout: 5000
      });

      // Still attempt to close the editor even if save failed
      if (onClose) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

/**
   * Cancel all changes and close the editor
   * Reverts only changes made during this editing session, preserving previously saved changes
   */
const cancelChanges = async () => {
  try {
    setIsSaving(true);

    // Only revert changes made during this editing session
    if (labelManager && labelManager.editedLabels && originalEditedLabels !== null) {
      console.log("[LabelEditor] Reverting only changes made during this session");
      
      // Get current labels to identify which ones need reverting
      const currentLabels = labelManager.getAllLabelGraphics ? labelManager.getAllLabelGraphics() : [];
      let revertCount = 0;
      
      // Revert each label to its state when the editor opened
      for (const label of currentLabels) {
        const labelId = labelManager.getLabelId(label);
        if (!labelId) continue;
        
        const originalData = originalEditedLabels.get(labelId);
        const currentData = labelManager.editedLabels.get(labelId);
        
        // If this label was modified during this session
        if (currentData && (!originalData || JSON.stringify(currentData) !== JSON.stringify(originalData))) {
          if (originalData) {
            // Restore to original edited state
            labelManager.editedLabels.set(labelId, { ...originalData });
            // Apply the original properties back to the graphic
            if (originalData.graphic && originalData.graphic.symbol) {
              label.symbol = originalData.graphic.symbol.clone();
            }
            if (originalData.graphic && originalData.graphic.geometry) {
              label.geometry = originalData.graphic.geometry.clone();
            }
          } else {
            // This label was newly edited during this session, remove it from tracking
            labelManager.editedLabels.delete(labelId);
            // Reset label to default appearance if it has resetLabelPosition method
            if (typeof labelManager.resetLabelPosition === 'function') {
              labelManager.resetLabelPosition(label);
            }
          }
          revertCount++;
        }
      }
      
      // Save the reverted state to localStorage
      if (typeof labelManager.savePositions === 'function') {
        await labelManager.savePositions(true);
      }
      
      // Force refresh to show reverted changes
      if (typeof labelManager.refreshLabels === 'function') {
        labelManager.refreshLabels();
      } else if (mapView?.graphics) {
        mapView.graphics.refresh();
      }
      
      console.log(`[LabelEditor] Reverted ${revertCount} labels to their state when editor opened`);
    }

    // Clear highlight boxes before closing
    const dragger = localLabelDragger || window.labelDragger || labelDragger;
    if (dragger && typeof dragger._removeHighlights === 'function') {
      console.log("[LabelEditor] Clearing highlight boxes on cancel");
      dragger._removeHighlights();
    }

    // Reset UI state
    setSelectedLabel(null);
    if (labelManager) {
      labelManager.selectedLabel = null;
    }
    setLabelText("");
    setFontSize(10);
    setPosition({ latitude: 0, longitude: 0 });
    setIsTextEditing(false);
    setSelectAllMode(false);
    if (isMultiDragMode) {
      togglePositionAdjustmentMode(false);
    }
    setLabelOptions(prev => ({
      ...prev,
      bold: false,
      whiteBackground: false
    }));
    setBackgroundOpacity(0.85);

    // Reset change tracking
    setHasUnsavedChanges(false);

    // Show cancellation message
    setStatusMessage({
      type: 'info',
      text: 'Session changes canceled - editor closing',
      timeout: 2000
    });

    // Close the editor after a brief delay
    setTimeout(() => {
      if (onClose) {
        console.log("[LabelEditor] Closing editor after cancellation");
        onClose();
      }
    }, 1500);

  } catch (error) {
    console.error("[LabelEditor] Failed to cancel changes:", error);

    // Clear highlight boxes even on error
    const dragger = localLabelDragger || window.labelDragger || labelDragger;
    if (dragger && typeof dragger._removeHighlights === 'function') {
      dragger._removeHighlights();
    }

    // Still close even if there's an error
    setStatusMessage({
      type: 'warning',
      text: 'Editor closing - some changes may not be fully reverted',
      timeout: 2000
    });

    // Reset change tracking and close
    setHasUnsavedChanges(false);

    if (onClose) {
      setTimeout(() => onClose(), 1000);
    }
  } finally {
    setIsSaving(false);
  }
};

  // --- Refresh Labels from Source ---
  const refreshLabels = () => {
    if (!labelManager || typeof labelManager.refreshLabels !== 'function') {
        console.warn("Cannot refresh labels: Manager or method missing.");
        return;
    }
    try {
        const result = labelManager.refreshLabels();
        console.log('[LabelEditor] Refresh result:', result);

        if (labelManager.getAllLabelGraphics) {
            setAllLabelsCount(labelManager.getAllLabelGraphics().length || 0);
        }
        setSelectedLabel(null); labelManager.selectedLabel = null; // Clear selection
        setLabelText(""); setFontSize(10); setPosition({ x: 0, y: 0 });
        setIsTextEditing(false); setSelectAllMode(false);
        if (isMultiDragMode) togglePositionAdjustmentMode(false);
        setLabelOptions(prev => ({ ...prev, bold: false, whiteBackground: false }));
        setBackgroundOpacity(0.85);
        setHasUnsavedChanges(false); // Reset changes tracking

        setStatusMessage({ type: 'success', text: `Refreshed labels from source.`, timeout: 3000 });
    } catch (error) {
        console.error('[LabelEditor] Error refreshing labels:', error);
        setStatusMessage({ type: 'error', text: `Refresh error: ${error.message}`, timeout: 5000 });
    }
  };

  // --- Reset All Local Changes ---
  const resetChanges = () => {
    if (!labelManager) {
        console.warn("Cannot reset labels: Manager missing.");
        return;
    }
    try {
        let resetCount = 0;

        // Clear the editedLabels map first
        if (labelManager.editedLabels) {
          resetCount = labelManager.editedLabels.size;
          labelManager.editedLabels.clear();
        }

        // Clear localStorage for label positions
        try {
          // Find and remove all label-related localStorage keys
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('labelPositions') || key.includes('editedLabels'))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          console.log(`[LabelEditor] Cleared ${keysToRemove.length} localStorage entries`);
        } catch (storageError) {
          console.warn('[LabelEditor] Could not clear localStorage:', storageError);
        }

        // If resetAllLabels exists, use it
        if (typeof labelManager.resetAllLabels === 'function') {
          const result = labelManager.resetAllLabels();
          console.log('[LabelEditor] Reset result:', result);
          resetCount = result?.count || resetCount;
        }

        // Reset all labels to their original positions and styles
        if (typeof labelManager.getAllLabelGraphics === 'function') {
          const allLabels = labelManager.getAllLabelGraphics();
          allLabels.forEach(label => {
            if (!label || !label.symbol) return;

            // Reset to default style
            const newSymbol = label.symbol.clone();

            // Reset font to default
            if (newSymbol.font) {
              newSymbol.font.size = 10;
              newSymbol.font.weight = 'normal';
            }

            // Remove background
            newSymbol.backgroundColor = null;
            newSymbol.borderLineColor = null;
            newSymbol.borderLineSize = 0;

            // Apply reset symbol
            label.symbol = newSymbol;

            // Reset visibility
            label.visible = true;
            if (label.attributes) {
              label.attributes.visible = true;
            }
          });

          // Force refresh
          if (labelManager.refreshLabels) {
            labelManager.refreshLabels();
          } else if (mapView?.graphics) {
            mapView.graphics.refresh();
          }
        }

        // Reset UI state
        setSelectedLabel(null);
        if (labelManager.selectedLabel !== undefined) {
          labelManager.selectedLabel = null;
        }
        setLabelText("");
        setFontSize(10);
        setPosition({ latitude: 0, longitude: 0 });
        setIsTextEditing(false);
        setSelectAllMode(false);
        if (isMultiDragMode) togglePositionAdjustmentMode(false);
        setLabelOptions(prev => ({ ...prev, bold: false, whiteBackground: false }));
        setBackgroundOpacity(0.85);
        setBorderSize(0.5);
        setHasUnsavedChanges(false); // Reset changes tracking

        // Force re-render
        setAllLabelsCount(labelManager.getAllLabelGraphics ? labelManager.getAllLabelGraphics().length : 0);

        setStatusMessage({ type: 'success', text: `Reset all labels to default settings.`, timeout: 3000 });
        console.log(`[LabelEditor] Reset ${resetCount} labels to default`);
    } catch (error) {
        console.error('[LabelEditor] Error resetting labels:', error);
        setStatusMessage({ type: 'error', text: `Reset error: ${error.message}`, timeout: 5000 });
    }
  };

  // --- Hide/Show Single Label ---
  const toggleSingleLabelVisibility = () => {
    if (!selectedLabel || !labelManager) {
      console.warn("[LabelEditor] Cannot toggle label visibility: No label selected or manager missing.");
      return;
    }

    try {
      // Toggle the visibility
      const newVisibleState = !selectedLabel.visible;
      selectedLabel.visible = newVisibleState;
      // CRITICAL: Also update attributes.visible so zoom handlers respect user's choice
      if (selectedLabel.attributes) {
        selectedLabel.attributes.visible = newVisibleState;
      }
      setHasUnsavedChanges(true);

      // Update in label manager tracking if available
      const labelId = labelManager.getLabelId(selectedLabel);
      if (labelId && labelManager.editedLabels) {
        const existingData = labelManager.editedLabels.get(labelId) || {};
        labelManager.editedLabels.set(labelId, {
          ...existingData,
          graphic: selectedLabel,
          visible: newVisibleState
        });
      }

      // Force refresh
      if (labelManager.refreshLabels) {
        labelManager.refreshLabels();
      } else if (mapView?.graphics) {
        mapView.graphics.refresh();
      }

      // Save changes
      if (typeof labelManager.savePositions === 'function') {
        labelManager.savePositions(true);
      }

      setStatusMessage({
        type: 'success',
        text: newVisibleState ? 'Label shown' : 'Label hidden',
        timeout: 2000
      });

      console.log(`[LabelEditor] ${newVisibleState ? 'Showed' : 'Hidden'} label: ${labelId}`);
    } catch (error) {
      console.error("[LabelEditor] Error toggling label visibility:", error);
      setStatusMessage({
        type: 'error',
        text: `Failed to toggle label visibility: ${error.message}`,
        timeout: 3000
      });
    }
  };

  // --- Reset Single Label ---
  const resetSingleLabel = () => {
    if (!selectedLabel || !labelManager || typeof labelManager.resetLabelPosition !== 'function') {
        console.warn("Cannot reset single label: No label selected or manager/method missing.");
        return;
    }
    try {
        const result = labelManager.resetLabelPosition(selectedLabel);
        console.log('[LabelEditor] Reset single label result:', result);

        if (result.success) {
            // Rely on watchSelection to update UI from manager's potentially reset graphic
            // Force immediate UI update for better feedback
            const resetGraphic = result.graphic || selectedLabel;
            if (resetGraphic?.symbol) {
                const symbol = resetGraphic.symbol;
                const font = symbol.font;
                setFontSize(font?.size || 10);
                const baseText = resetGraphic.attributes?.hasCustomFormat ? resetGraphic.attributes.originalLabel || symbol.text : symbol.text;
                setLabelText(baseText || "");
                setPosition({ latitude: resetGraphic.geometry?.y || 0, longitude: resetGraphic.geometry?.x || 0 }); // Use reset coordinates
                setLabelOptions(prev => ({
                    ...prev,
                    bold: font?.weight === 'bold',
                    whiteBackground: hasBackgroundRectangle(symbol)
                }));
                setIsTextEditing(false);
                setBackgroundOpacity(getBackgroundOpacity(symbol));
            }
            setHasUnsavedChanges(true); // Mark as changed
            setStatusMessage({ type: 'success', text: 'Label reset to default', timeout: 3000 });
        } else {
            setStatusMessage({ type: 'error', text: result.message || 'Could not reset label', timeout: 5000 });
        }
    } catch (error) {
        console.error('[LabelEditor] Error resetting single label:', error);
        setStatusMessage({ type: 'error', text: `Reset error: ${error.message}`, timeout: 5000 });
    }
  };

  // --- Render Status Message ---
  const renderStatusMessage = () => {
    if (!statusMessage) return null;
    const bgColor =
      statusMessage.type === 'error' ? 'bg-red-100 dark:bg-red-900/40' :
      statusMessage.type === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/40' :
      statusMessage.type === 'info' ? 'bg-blue-100 dark:bg-blue-900/40' :
      'bg-green-100 dark:bg-green-900/40';
    const textColor =
      statusMessage.type === 'error' ? 'text-red-700 dark:text-red-300' :
      statusMessage.type === 'warning' ? 'text-yellow-700 dark:text-yellow-300' :
      statusMessage.type === 'info' ? 'text-blue-700 dark:text-blue-300' :
      'text-green-700 dark:text-green-300';
    let iconComponent;
    if (statusMessage.type === 'error' || statusMessage.type === 'warning') {
      iconComponent = <AlertTriangle className="h-4 w-4 flex-shrink-0" />;
    } else if (statusMessage.type === 'info') {
      iconComponent = <Globe className="h-4 w-4 flex-shrink-0" />;
    } else {
      iconComponent = <Check className="h-4 w-4 flex-shrink-0" />;
    }
    return (
      <div className={`mb-4 p-3 rounded-md ${bgColor} ${textColor} flex items-center space-x-2 text-sm`}>
        {iconComponent}
        <span>{statusMessage.text}</span>
      </div>
    );
  };

  // --- RENDER ---
  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="flex-none p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold">Label Editor</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Click label to edit. Use position selector or drag on map.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* Refresh Button */}
            <button
              onClick={refreshLabels}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              title="Refresh labels from source"
              disabled={isSaving}
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        {/* Select All Labels, Reset All Labels, and Labels Visible Controls */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {/* Left side: Select All and Reset All buttons */}
          <div className="flex items-center space-x-2">
            {/* Select All Labels Button */}
            <button
              onClick={toggleSelectAllMode}
              className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center ${
                selectAllMode
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300'
                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
              }`}
              title={selectAllMode ? "Exit Select All Mode" : "Select All Labels"}
              disabled={isMultiDragMode}
            >
              <Layers size={16} className="mr-1.5" />
              {selectAllMode ? "Exit Select All" : "Select All Labels"}
            </button>

            {/* Reset All Labels Button */}
            <button
              onClick={resetChanges}
              className="px-3 py-1.5 rounded-md text-sm font-medium flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
              title="Reset all label changes back to defaults"
              disabled={isMultiDragMode || isSaving}
            >
              <RefreshCw size={16} className="mr-1.5" />
              Reset All
            </button>
          </div>

          {/* Labels Visible Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Labels Visible</span>
            <button
              type="button"
              onClick={toggleAllLabelsVisibility}
              className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${
                allLabelsVisible ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
              title={allLabelsVisible ? "Hide all labels" : "Show all labels"}
            >
              <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                allLabelsVisible ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mode Indicator Banners */}
      {selectAllMode && (
        <div className="bg-blue-50 dark:bg-blue-900/30 px-4 py-2 border-b border-blue-100 dark:border-blue-800">
          <div className="flex items-center text-blue-700 dark:text-blue-300">
            <Globe className="h-4 w-4 mr-2" />
            <span className="text-sm font-medium">
              Select All Mode: Adjusting {allLabelsCount} labels simultaneously
            </span>
          </div>
        </div>
      )}
 
      {/* Status Message Area */}
      {statusMessage && (
        <div className="px-4 pt-4">{renderStatusMessage()}</div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Select All Mode Controls */}
        {selectAllMode ? (
          <div className="space-y-4 p-4 border rounded-lg border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
             <div className="flex items-center space-x-2 mb-4">
                <Globe className="h-5 w-5 text-blue-500" />
                <h3 className="text-base font-medium text-blue-700 dark:text-blue-300">Global Label Settings</h3>
             </div>
             {/* Font Size Slider for All Labels */}
             <div className="space-y-2">
                <div className="flex justify-between items-center">
                   <label className="text-sm font-medium flex items-center"><Sliders className="mr-2 h-4 w-4" /> Global Font Size</label>
                   <span className="text-sm font-mono bg-blue-200 dark:bg-blue-800 px-2 py-0.5 rounded">{fontSize}px</span>
                </div>
                <input
                   type="range" min="8" max="24" step="1" value={fontSize}
                   onChange={(e) => updateFontSize(parseInt(e.target.value, 10))}
                   className="w-full h-2 bg-blue-200 dark:bg-blue-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                   Applies to all {allLabelsCount} labels
                </p>
             </div>
             {/* Bold Text and White Background options for All Labels */}
             <div className="mt-4 space-y-3">
                {/* Bold Toggle */}
                <div className="flex items-center justify-between">
                   <label className="text-sm font-medium text-blue-700 dark:text-blue-300 cursor-pointer flex items-center"
                      onClick={() => updateLabelOption('bold', !labelOptions.bold)}>
                      <span className="font-bold mr-1">B</span> Bold Text (All)
                   </label>
                   <button type="button" onClick={() => updateLabelOption('bold', !labelOptions.bold)} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${labelOptions.bold ? 'bg-blue-600 dark:bg-blue-500' : 'bg-blue-200 dark:bg-blue-700'}`}>
                      <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${labelOptions.bold ? 'translate-x-6' : 'translate-x-1'}`} />
                   </button>
                </div>

                {/* White Background Toggle */}
                <div className="flex items-center justify-between">
                   <label className="text-sm font-medium text-blue-700 dark:text-blue-300 cursor-pointer"
                      onClick={() => updateLabelOption('whiteBackground', !labelOptions.whiteBackground)}>
                      White Background (All)
                   </label>
                   <button type="button" onClick={() => updateLabelOption('whiteBackground', !labelOptions.whiteBackground)} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${labelOptions.whiteBackground ? 'bg-blue-600 dark:bg-blue-500' : 'bg-blue-200 dark:bg-blue-700'}`}>
                      <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${labelOptions.whiteBackground ? 'translate-x-6' : 'translate-x-1'}`} />
                   </button>
                </div>
             </div>
             <button onClick={toggleSelectAllMode} className="w-full py-1.5 px-4 mt-4 rounded-md text-sm font-medium bg-white text-blue-600 hover:bg-blue-50 dark:bg-blue-800 dark:text-blue-300 dark:hover:bg-blue-700 border border-blue-300 dark:border-blue-700">
                Exit Select All Mode
             </button>
          </div>
        ) : (
          /* Single Label Editing Controls */
          selectedLabel && selectedLabel.symbol && selectedLabel.attributes ? (
            <div className="space-y-4 p-4 border rounded-lg border-gray-200 dark:border-gray-700">
              {/* Label Text Edit */}
              <div>
                <label className="text-sm font-medium mb-1 block flex items-center">
                  <Type className="mr-2 h-4 w-4 text-gray-500" /> Label Text
                </label>
                {isTextEditing ? (
                  <div className="flex items-center space-x-2">
                    <input
                      ref={textInputRef} type="text" value={labelText} // Base text state
                      onChange={(e) => setLabelText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { updateLabelText(labelText); setIsTextEditing(false); } else if (e.key === "Escape") { setLabelText(selectedLabel.attributes?.originalLabel || selectedLabel.symbol?.text || ""); setIsTextEditing(false); }}}
                      onBlur={() => { updateLabelText(labelText); setIsTextEditing(false); }}
                      className="flex-grow px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Enter base label text"
                    />
                    <button onClick={() => { updateLabelText(labelText); setIsTextEditing(false); }} className="p-1.5 rounded-md bg-green-500 text-white hover:bg-green-600" title="Save"><Check className="h-4 w-4" /></button>
                    <button onClick={() => { setLabelText(selectedLabel.attributes?.originalLabel || selectedLabel.symbol?.text || ""); setIsTextEditing(false); }} className="p-1.5 rounded-md bg-gray-500 text-white hover:bg-gray-600" title="Cancel"><XIcon className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 min-h-[38px]">
                    <p className="text-sm truncate flex-1 mr-2" title={selectedLabel.symbol?.text || ""}>
                      {selectedLabel.symbol?.text || "(No text)"} {/* Display potentially formatted text */}
                    </p>
                    <button onClick={() => setIsTextEditing(true)} className="p-1 rounded-full text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/50" title="Edit Text">
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                )}
                 {selectedLabel.attributes?.hasCustomFormat && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Editing base text. Variables like ({selectedLabel.attributes?.variableName}) are added automatically.</p>}
              </div>

              {/* Font Size Slider */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium flex items-center"><Sliders className="mr-2 h-4 w-4" /> Font Size</label>
                  <span className="text-sm font-mono bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">{fontSize}px</span>
                </div>
                <input type="range" min="8" max="24" step="1" value={fontSize}
                  onChange={(e) => updateFontSize(parseInt(e.target.value, 10))}
                  className="w-full h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  disabled={isTextEditing || isMultiDragMode} // Disable during text edit or position adjustment
                />
              </div>

              {/* Drag Instruction */}
              <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                Use Right Click to Drag and Reposition labels on Map
              </p>

              {/* Style Options for Single Label */}
               <div className="mt-4 space-y-3 border-t pt-4 dark:border-gray-600">
                   <h4 className="text-sm font-medium mb-2 text-gray-600 dark:text-gray-400">Style Options</h4>
                   {/* Bold Toggle */}
                   <div className="flex items-center justify-between">
                       <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer flex items-center"
                           onClick={() => updateLabelOption('bold', !labelOptions.bold)}>
                           <span className="font-bold mr-1">B</span> Bold Text
                       </label>
                       <button type="button" onClick={() => updateLabelOption('bold', !labelOptions.bold)} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${labelOptions.bold ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}>
                           <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${labelOptions.bold ? 'translate-x-6' : 'translate-x-1'}`} />
                       </button>
                   </div>

                   {/* White Background Toggle */}
                   <div className="flex items-center justify-between">
                       <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                           onClick={() => updateLabelOption('whiteBackground', !labelOptions.whiteBackground)}>
                           White Background
                       </label>
                       <button type="button" onClick={() => updateLabelOption('whiteBackground', !labelOptions.whiteBackground)} className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${labelOptions.whiteBackground ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}>
                           <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${labelOptions.whiteBackground ? 'translate-x-6' : 'translate-x-1'}`} />
                       </button>
                   </div>

                   {/* Point Shape Selector */}
                   <div className="mt-3 pt-3 border-t dark:border-gray-600">
                     <div className="flex items-center justify-between">
                       <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                         Point Shape
                       </label>
                       <select
                         value={dataPointShape}
                         onChange={(e) => updateDataPointShape(e.target.value)}
                         className="px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                         disabled={isTextEditing || isMultiDragMode}
                       >
                         <option value="circle">Circle</option>
                         <option value="square">Square</option>
                         <option value="diamond">Diamond</option>
                         <option value="triangle">Triangle</option>
                         <option value="cross">Cross</option>
                         <option value="x">X</option>
                       </select>
                     </div>
                     <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                       Change the shape of this data point marker
                     </p>
                   </div>
               </div>

              {/* Hide/Show Label Toggle */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-gray-600">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer flex items-center"
                  onClick={toggleSingleLabelVisibility}>
                  {selectedLabel?.visible === false ? (
                    <>
                      <EyeOff className="mr-2 h-4 w-4 text-gray-500" /> Label Hidden
                    </>
                  ) : (
                    <>
                      <Eye className="mr-2 h-4 w-4 text-gray-500" /> Label Visible
                    </>
                  )}
                </label>
                <button
                  type="button"
                  onClick={toggleSingleLabelVisibility}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${
                    selectedLabel?.visible !== false ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  disabled={isTextEditing || isMultiDragMode}
                >
                  <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                    selectedLabel?.visible !== false ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Reset Single Label Button */}
              <button onClick={resetSingleLabel} className="w-full py-1.5 px-4 mt-4 rounded-md text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600"
                disabled={isTextEditing || isMultiDragMode}>
                Reset This Label
              </button>
            </div>
          ) : (
            // Placeholder when no label is selected
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
              <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                <Type className="h-10 w-10 text-gray-400 dark:text-gray-500" />
              </div>
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">No Label Selected</h4>
              <p className="mt-2 text-sm">Click a label on the map to edit its properties.</p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Use Right Click to Drag and Reposition labels on Map</p>

              {selectedLabel && (!selectedLabel.symbol || !selectedLabel.attributes) && (
                <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">(Selected graphic missing symbol/attributes)</p>
              )}
            </div>
          )
        )}
      </div>

      {/* Bottom padding to prevent content being hidden behind fixed footer */}
      <div className="pb-20"></div>
      
      {/* Fixed Footer with Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 dark:bg-gray-900 py-3 px-4 border-t border-gray-700 shadow-lg z-10">
        <div className="container mx-auto max-w-screen-lg flex items-center justify-between">
          {/* Unsaved Changes Indicator */}
          {hasUnsavedChanges && (
            <div className="flex items-center text-sm text-amber-400">
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
              </svg>
              Unsaved changes
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex ml-auto space-x-3">
            <button
              onClick={cancelChanges}
              className="px-4 py-2 text-sm bg-transparent hover:bg-gray-700 text-white rounded border border-gray-600"
            >
              Cancel
            </button>

            <button
              onClick={applyChanges}
              className={`px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded flex items-center ${
                isSaving ? 'opacity-75 cursor-not-allowed' : ''
              }`}
              disabled={!hasUnsavedChanges || isSaving}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Apply Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabelEditor;